import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { appsAPI } from '../api/appsAPI';
import { Logo_black_iconOnly } from '../../../components/Logo/Logo';
import { getTutorialIconLabel } from '../utils/appDisplayUtils';
import { useProductBackHandler } from '../../../components/ProductBackButton/ProductBackProvider';
import './CreatorPage.css';

/**
 * Page showing all apps by a single creator. Used from landing (/creator/:slug) and app store (/apps-store/creator/:slug).
 */
const CreatorPage = ({ fromLanding = false }) => {
  const { creatorSlug } = useParams();
  const navigate = useNavigate();
  const creatorName = creatorSlug ? decodeURIComponent(creatorSlug) : '';
  const [apps, setApps] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!creatorName) {
      setLoading(false);
      setError('No creator specified');
      return;
    }
    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch apps and profile concurrently
        const [appsData, profileRes] = await Promise.all([
          appsAPI.getAppsByCreatorPublic(creatorName),
          appsAPI.getCreatorProfilePublic(creatorName).catch(err => {
            console.error('Failed to load creator profile:', err);
            return { success: true, profile: null };
          })
        ]);

        if (!cancelled) {
          setApps(Array.isArray(appsData) ? appsData : []);
          if (profileRes && profileRes.success) {
            setProfile(profileRes.profile);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Failed to load apps');
          setApps([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [creatorName]);

  const backUrl = fromLanding ? '/creators' : '/apps-store/creators';
  const handleProductBack = useCallback(() => {
    if (window.history.length > 1) navigate(-1);
    else navigate(backUrl);
  }, [navigate, backUrl]);
  useProductBackHandler(handleProductBack);

  const handleAppClick = (appId) => {
    if (fromLanding) {
      navigate(`/store/${appId}`);
    } else {
      navigate(`/apps-store/${appId}`);
    }
    window.scrollTo(0, 0);
  };

  return (
    <div className="creator-page app-store-view">
      <header className="creator-page-header app-store-header app-store-header-full">
        <div className="creator-page-header-inner">
          <div className="creator-page-logo-wrap" onClick={() => navigate(backUrl)} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && navigate(backUrl)} aria-label="Home">
            <Logo_black_iconOnly />
          </div>
          <h1 className="creator-page-header-title">Creator Profile</h1>
        </div>
      </header>

      <main className="creator-page-main">
        <div className="creator-page-content">
          <h1 className="creator-page-title">Apps by {creatorName || '…'}</h1>

          {loading && <p className="creator-page-message">Loading…</p>}
          {error && <p className="creator-page-message creator-page-error">{error}</p>}

          {!loading && !error && profile && (
            <div className="creator-profile-card">
              <div className="creator-profile-left">
                <div className="creator-avatar">
                  {profile.first_name ? (
                    `${profile.first_name[0]}${profile.last_name ? profile.last_name[0] : ''}`.toUpperCase()
                  ) : (
                    creatorName[0].toUpperCase()
                  )}
                </div>
                <div className="creator-details">
                  <h2 className="creator-display-name">
                    {profile.first_name ? `${profile.first_name} ${profile.last_name}` : creatorName}
                  </h2>
                  <p className="creator-headline">
                    {profile.professional_summary 
                      ? profile.professional_summary.replace(/Author/g, 'Creator').replace(/author/g, 'creator') 
                      : 'Interactive Case Study Creator'}
                  </p>
                  {(profile.city || profile.country || profile.primary_subject) && (
                    <div className="creator-badges">
                      {(profile.city || profile.country) && (
                        <span className="creator-badge location-badge">
                          📍 {profile.city}{profile.city && profile.country ? ', ' : ''}{profile.country}
                        </span>
                      )}
                      {profile.primary_subject && (
                        <span className="creator-badge subject-badge">
                          🎓 {profile.primary_subject.toUpperCase()}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="creator-profile-right">
                {profile.bio && <p className="creator-bio">{profile.bio}</p>}
                
                <div className="creator-links-and-stats">
                  <div className="creator-stats">
                    <span className="stats-label">Published:</span>
                    <span className="stats-value">{apps.length} {apps.length === 1 ? 'App' : 'Apps'}</span>
                  </div>
                  <div className="creator-links">
                    {profile.linkedin_url && (
                      <a href={profile.linkedin_url} target="_blank" rel="noopener noreferrer" className="creator-link linkedin-link">
                        <svg className="link-icon" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                          <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.779-1.75-1.75s.784-1.75 1.75-1.75 1.75.779 1.75 1.75-.784 1.75-1.75 1.75zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                        </svg>
                        LinkedIn
                      </a>
                    )}
                    {profile.personal_website && (
                      <a href={profile.personal_website.startsWith('http') ? profile.personal_website : `https://${profile.personal_website}`} target="_blank" rel="noopener noreferrer" className="creator-link website-link">
                        <svg className="link-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="2" y1="12" x2="22" y2="12"/>
                          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                        </svg>
                        Website
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {!loading && !error && apps.length === 0 && (
            <p className="creator-page-message">No apps from this creator yet.</p>
          )}

          {!loading && !error && apps.length > 0 && (
            <div className="creator-apps-sections">
              {/* 1. Case Study Apps Section */}
              {apps.filter(app => app.app_type === 'case').length > 0 && (
                <div className="creator-section">
                  <h2 className="creator-page-section-title">Case Study Apps</h2>
                  <div className="creator-page-grid">
                    {apps.filter(app => app.app_type === 'case').map((app) => {
                      const imageUrl = app.images?.medium || app.images?.large || app.images?.small || null;
                      const rating = app.rating !== undefined ? app.rating : 4.5;
                      const title = app.title || app.exam_name || 'App';
                      return (
                        <div
                          key={app.id}
                          role="button"
                          tabIndex={0}
                          className="creator-page-card"
                          onClick={() => handleAppClick(app.id)}
                          onKeyDown={(e) => e.key === 'Enter' && handleAppClick(app.id)}
                        >
                          <div className="creator-page-card-image">
                            <img src={imageUrl || '/logo192.png'} alt={title} />
                          </div>
                          <div className="creator-page-card-info">
                            <h3 className="creator-page-card-title">{title}</h3>
                            <div className="creator-page-card-meta">
                              <span className="creator-page-card-rating">{rating.toFixed(1)} ★</span>
                              <span className="creator-page-card-category">{app.subject || app.industry || 'Case Study'}</span>
                            </div>
                            <div className="creator-page-card-price">Free</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 2. Practice & Utility Apps Section */}
              {apps.filter(app => app.app_type !== 'case').length > 0 && (
                <div className="creator-section" style={{ marginTop: '40px' }}>
                  <h2 className="creator-page-section-title">Practice & Utility Apps</h2>
                  <div className="creator-page-grid">
                    {apps.filter(app => app.app_type !== 'case').map((app) => {
                      const isProductivity = app.app_type === 'tutorial' || app.app_type === 'productivity';
                      const imageUrl = app.images?.medium || app.images?.large || app.images?.small || null;
                      const rating = app.rating !== undefined ? app.rating : 4.5;
                      const title = app.title || app.exam_name || 'App';
                      return (
                        <div
                          key={app.id}
                          role="button"
                          tabIndex={0}
                          className="creator-page-card"
                          onClick={() => handleAppClick(app.id)}
                          onKeyDown={(e) => e.key === 'Enter' && handleAppClick(app.id)}
                        >
                          <div className="creator-page-card-image">
                            {isProductivity ? (
                              <span className="creator-page-card-icon-text">{getTutorialIconLabel(app)}</span>
                            ) : (
                              <img src={imageUrl || '/logo192.png'} alt={title} />
                            )}
                          </div>
                          <div className="creator-page-card-info">
                            <h3 className="creator-page-card-title">{title}</h3>
                            <div className="creator-page-card-meta">
                              <span className="creator-page-card-rating">{rating.toFixed(1)} ★</span>
                              <span className="creator-page-card-category">{app.subject || app.industry || 'Practice'}</span>
                            </div>
                            <div className="creator-page-card-price">Free</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CreatorPage;
